import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Button } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';
import { useAppDispatch, useAppSelector } from '../../../../utils';
import {
  activeSubLayerData,
  isTrueColorOn,
} from '../../../../selectors/fhaSelectors';
import { getFhaTimelapseFrames } from '../../../../selectors/fhaTimelapseSelectors';
import { getCurrentSeason } from '../../../../selectors/seasonSelectors';
import { setActiveSubLayer, setFhaTimelapsePlaying } from '../../../../actions/layersActions';
import { FHA_KEY, FhaSegment } from '../../../../types/Fha';
import PlayIcon from '../../../../images/timelapse-play.svg';
import PauseIcon from '../../../../images/timelapse-pause.svg';
import { useImagePreloader } from './useImagePreloader';
import { useScrubber } from './useScrubber';
import styles from './FHATimelapse.css';

export const FRAME_INTERVAL_MS = 1250;
export const MAP_NARROW_THRESHOLD_PX = 740;

export type FHATimelapseProps = {
  index: number;
};

type ActiveSubLayer = {
  date: string;
  layer: FhaSegment;
  segmentSelected: FhaSegment;
};

function formatDisplayDate(dateStr: string, locale: string): string {
  try {
    return new Date(dateStr).toLocaleDateString(locale ?? 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

const FHATimelapseComponent = ({ index }: FHATimelapseProps) => {
  const dispatch = useAppDispatch();
  const selectorProps = useMemo(() => ({ index }), [index]);

  const { eventDates, imageUris, activeImageUri } = useAppSelector((state) =>
    getFhaTimelapseFrames(state, selectorProps)
  );
  const activeData = useAppSelector(
    (state) => activeSubLayerData(state, selectorProps) as ActiveSubLayer | undefined
  );
  const isInTrueColor = useAppSelector((state) => isTrueColorOn(state, selectorProps));
  const currentSeason = useAppSelector((state) => getCurrentSeason(state, selectorProps));
  const locale = useAppSelector((state) => state.app.locale);

  const { progress: preloadProgress, isPreloading: isTilePreloading } =
    useImagePreloader(imageUris, activeImageUri);

  const timelineDates = useMemo(() => [...eventDates].reverse(), [eventDates]);
  const totalFrames = timelineDates.length;
  const hasNoFrames = totalFrames === 0;
  const isLoading = hasNoFrames || isTilePreloading;

  const reduxFrameIndex = activeData
    ? Math.max(0, timelineDates.findIndex((d) => d === activeData.date))
    : 0;

  const [currentFrameIndex, setCurrentFrameIndex] = useState(reduxFrameIndex);
  const [isPlaying, setIsPlaying] = useState(false);

  const setPlaying = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setIsPlaying((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        dispatch(setFhaTimelapsePlaying(resolved));
        return resolved;
      });
    },
    [dispatch]
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const [isNarrow, setIsNarrow] = useState(false);

  // Track the map container width so we can raise the overlay when the
  // surrounding area is narrow (small viewport, expanded shell menu, split view, etc.).
  useEffect(() => {
    const parent = containerRef.current?.parentElement;
    if (!parent || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => {
      setIsNarrow(entry.contentRect.width <= MAP_NARROW_THRESHOLD_PX);
    });
    observer.observe(parent);
    return () => observer.disconnect();
  }, []);

  // Pause when True Color mode is activated
  useEffect(() => {
    if (isInTrueColor) {
      setPlaying(false);
    }
  }, [isInTrueColor, setPlaying]);

  // Pause when the active segment changes (e.g. user switches Scouting ↔ Vegetation tabs)
  useEffect(() => {
    setPlaying(false);
  }, [activeData?.segmentSelected, setPlaying]);

  // Reset Redux flag on unmount so leaving the FHA layer doesn't leave a stale flag
  useEffect(
    () => () => {
      dispatch(setFhaTimelapsePlaying(false));
    },
    [dispatch]
  );

  const dispatchFrame = useCallback(
    (frameIndex: number) => {
      const date = timelineDates[frameIndex];
      if (!date || !activeData) return;
      dispatch(
        setActiveSubLayer(
          FHA_KEY,
          {
            date,
            layer: activeData.layer,
            segmentSelected: activeData.segmentSelected,
          },
          index,
          currentSeason
        )
      );
    },
    [timelineDates, activeData, dispatch, index, currentSeason]
  );

  // Auto-play interval
  useEffect(() => {
    if (!isPlaying || isLoading) {
      return undefined;
    }

    const id = setInterval(() => {
      setCurrentFrameIndex((prev) => {
        const next = (prev + 1) % totalFrames;
        dispatchFrame(next);
        return next;
      });
    }, FRAME_INTERVAL_MS);

    return () => clearInterval(id);
  }, [isPlaying, isLoading, totalFrames, dispatchFrame]);

  const seekToPercent = useCallback(
    (percent: number) => {
      if (totalFrames <= 0) return;
      const newIndex = Math.round(percent * (totalFrames - 1));
      const clampedIndex = Math.max(0, Math.min(newIndex, totalFrames - 1));
      setCurrentFrameIndex(clampedIndex);
      dispatchFrame(clampedIndex);
    },
    [totalFrames, dispatchFrame]
  );

  const handleScrubStart = useCallback(() => setPlaying(false), [setPlaying]);

  const {
    trackRef,
    isDragging,
    hoverPercent,
    handleTrackMouseDown,
    handleTrackMouseMove,
    handleTrackMouseLeave,
  } = useScrubber({ isLoading, onSeek: seekToPercent, onScrubStart: handleScrubStart });

  // Sync local frame with Redux when idle (e.g. user clicks a card in the panel)
  useEffect(() => {
    if (!isPlaying && !isDragging) {
      setCurrentFrameIndex(reduxFrameIndex);
    }
  }, [reduxFrameIndex, isPlaying, isDragging]);

  const handlePlayPause = useCallback(() => {
    if (isLoading) return;
    if (!isPlaying && isInTrueColor && activeData) {
      dispatch(
        setActiveSubLayer(
          FHA_KEY,
          {
            date: activeData.date,
            layer: activeData.segmentSelected,
            segmentSelected: activeData.segmentSelected,
          },
          index,
          currentSeason
        )
      );
    }
    setPlaying((prev) => !prev);
  }, [isLoading, isPlaying, isInTrueColor, activeData, dispatch, index, currentSeason, setPlaying]);

  let progressPercent = 0;
  if (!isLoading && totalFrames > 1) {
    progressPercent = (currentFrameIndex / (totalFrames - 1)) * 100;
  } else if (isTilePreloading) {
    progressPercent = preloadProgress ?? 0;
  }

  const firstDate = !hasNoFrames
    ? formatDisplayDate(timelineDates[0], locale)
    : '';
  const lastDate = !hasNoFrames
    ? formatDisplayDate(timelineDates[totalFrames - 1], locale)
    : '';

  const loadingText = isTilePreloading
    ? `${getMessage('timelapse.preparing')} ${preloadProgress}%`
    : getMessage('timelapse.preparing');

  const tooltipIndex =
    hoverPercent !== null && !isDragging
      ? Math.round(hoverPercent * (totalFrames - 1))
      : null;
  const tooltipDate =
    tooltipIndex !== null && timelineDates[tooltipIndex]
      ? formatDisplayDate(timelineDates[tooltipIndex], locale)
      : null;

  return (
    <div
      ref={containerRef}
      className={classNames(styles.container, { [styles.containerNarrow]: isNarrow })}
      data-test-id="fha-timelapse"
    >
      <Button
        aria-label={
          isPlaying ? getMessage('timelapse.pause') : getMessage('timelapse.play')
        }
        className={styles.playButton}
        data-test-id="fha-timelapse-play-btn"
        disabled={isLoading}
        mode="map"
        onClick={handlePlayPause}
        type="button"
      >
        {isPlaying ? (
          <PauseIcon
            aria-hidden="true"
            data-test-id="fha-timelapse-pause-icon"
            fill="currentColor"
            height="20"
            width="20"
          />
        ) : (
          <PlayIcon
            aria-hidden="true"
            data-test-id="fha-timelapse-play-icon"
            fill="currentColor"
            height="20"
            width="20"
          />
        )}
      </Button>

      <div className={styles.progressSection}>
        {tooltipDate && (
          <div
            className={styles.tooltip}
            data-test-id="fha-timelapse-tooltip"
            style={{ left: `${(hoverPercent ?? 0) * 100}%` }}
          >
            {tooltipDate}
          </div>
        )}

        <div
          className={classNames(styles.track, {
            [styles.trackLoading]: isLoading,
            [styles.trackDragging]: isDragging,
          })}
          data-test-id="fha-timelapse-track"
          onMouseDown={handleTrackMouseDown}
          onMouseLeave={handleTrackMouseLeave}
          onMouseMove={handleTrackMouseMove}
          ref={trackRef}
          role="slider"
          tabIndex={isLoading ? -1 : 0}
          aria-valuemin={0}
          aria-valuemax={Math.max(0, totalFrames - 1)}
          aria-valuenow={currentFrameIndex}
        >
          <div className={styles.fill} style={{ width: `${progressPercent}%` }} />
          {!isLoading && (
            <div
              className={classNames(styles.handle, { [styles.handleDragging]: isDragging })}
              data-test-id="fha-timelapse-handle"
              style={{ left: `${progressPercent}%` }}
            />
          )}
        </div>

        <div className={styles.dateRow}>
          {isLoading ? (
            <span
              className={styles.loadingText}
              data-test-id="fha-timelapse-loading"
            >
              {loadingText}
            </span>
          ) : (
            <>
              <span className={styles.dateLabel}>{firstDate}</span>
              <span className={styles.dateLabel}>{lastDate}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const FHATimelapse = React.memo(FHATimelapseComponent);
FHATimelapse.displayName = 'FHATimelapse';

export default FHATimelapse;
